import * as React from "react";
import { useNavigate } from "react-router-dom";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { colors } from "../../styles/constants";
import { Button } from "../../components/atoms/Button";
import { Header } from "../../components/atoms/Header";
import { FormField } from "../../components/molecules/FormField";
import { FormStack } from "../../components/molecules/FormStack";
import { SideImageTemplate } from "@/templates/SideImageTemplate";
import { getExpressData } from "./express-data";
import { navigateToURL } from "./navigateToURL";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useBrandingContext } from "@/contexts/BrandingContext";
import { sessionFromUrl } from "@/helpers/session-helpers";
import { setLocalSession } from "@/utils/session";
import { ThirdPartyMarketing } from "@/components/organisms/ThirdPartyMarketing";
export type ExpressFormSubmittedPageProps = {
  serviceProvider: { companyName: string };
};

export const ExpressFormSubmittedPage: React.FC<ExpressFormSubmittedPageProps> = ({
  serviceProvider,
}) => {
  useScrollToTop();
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const done = React.useCallback(() => {
    setBusy(true);
    navigateToURL(navigate, "/status");
  }, [setBusy, navigate]);

  return <ExpressFormSubmittedPageView busy={busy} serviceProvider={serviceProvider} done={done} />;
};

export type ExpressFormSubmittedPageViewProps = {
  busy?: boolean;
  serviceProvider: { companyName: string };
  done: () => void;
};

export const ExpressFormSubmittedPageView: React.FC<ExpressFormSubmittedPageViewProps> = ({
  busy,
  serviceProvider,
  done,
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));
  const { brand } = useBrandingContext();

  const expressData = getExpressData();
  const session = sessionFromUrl(expressData.formUrl);
  session && setLocalSession(session);

  return (
    <SideImageTemplate
      headerType="help"
      busy={busy}
      image={<img src="/images/stock/express-form-submitted.svg" width="100%" alt="Family" />}
    >
      <Stack rowGap={4}>
        {matches ? (
          <>
            <Header level={1} color={colors.accentTeal}>
              {serviceProvider.companyName} successfully notified
            </Header>

            <Header level={2}>Would you like to notify more companies?</Header>
          </>
        ) : (
          <>
            <Stack columnGap={1} direction="row">
              <CheckCircleOutlineOutlinedIcon fontSize="small" sx={{ color: colors.lightTeal }} />
              <Typography variant="body1" color={colors.accentTeal}>
                <b>{serviceProvider.companyName} successfully notified</b>
              </Typography>
            </Stack>
            <Header level={1} color={colors.bodyText}>
              Would you like to notify more companies?
            </Header>
          </>
        )}

        <Typography variant="body1">
          Click ‘Go to dashboard’ below to go to your account dashboard and check the progress of
          your request. You can also use it to add more accounts and notify other companies of the
          death in one go (banks, insurers, utilities &amp; many more), and to receive updates in
          one, secure place. {brand}’s service is free.
        </Typography>

        <FormStack spacing={4}>
          <ThirdPartyMarketing />
          <FormField twoThirdsWidth>
            {session && (
              <Button variant="primary" size="large" disabled={busy} onClick={done}>
                Go to dashboard
              </Button>
            )}
          </FormField>
        </FormStack>
      </Stack>
    </SideImageTemplate>
  );
};
